import { Trans } from "@lingui/react/macro";
import { t } from "@lingui/core/macro";
import { LimeSelect } from "@/Components/NextBase/LimeSelect";
import { useLocation, useNavigate, useSearchParams } from "react-router";
import {
  addToast,
  Button,
  Chip,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Spinner,
  useDisclosure,
} from "@heroui/react";
import { api, queryClient } from "@/lib/api-client";
import { useForm } from "@mantine/form";
import { POSPinSetupDialog } from "./Invoices/POS/PinSetupDialog";
import { useEffect, useRef } from "react";
import { LimeInputOtp } from "@/Components/NextBase/LimeInputOtp";
import { LimeAlert } from "@/Components/NextBase/LimeAlert";
import useAccountInfo from "@/hooks/useAccountInfo";
import { parseAsBoolean, parseAsString, useQueryState } from "nuqs";
import { useClientFeature } from "@/hooks/useClientFeature";
import type { TaxSystem } from "@/server-types";
import { isMobileSafari } from "react-device-detect";
import { isNativePlatform } from "@/utils/native";
import { useLogout } from "@/hooks/useLogout";
import useUserPermissionsStore from "@/stores/useUserPermissionsStore";
import { isArchivedOrganization } from "./archivedOrganization";
import {
  canAuthorizeOnPos,
  computeHasNoAuthorizableUsers,
} from "./authorizableUsers";

export type TaxRegisterAuthorizationForm = {
  userId: number | null;
  PIN: string;
  fiscalizationId: number | null | undefined;
  taxSystem?: TaxSystem;
};

export const TaxRegisterAuthorizationDialog = ({
  handleSuccessfulAuthorization,
  requiresSetupCompleted = true,
  redirectUrl: redirectUrlProp,
  onCancel,
}: {
  handleSuccessfulAuthorization?: () => void;
  requiresSetupCompleted?: boolean;
  redirectUrl?: string;
  /** Replaces the redirect on cancel, for a caller that opened the dialog on demand. */
  onCancel?: () => void;
}) => {
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const [preselectedUserId] = useQueryState("auid", parseAsString);
  const [redirectUrl] = useQueryState("redirectUrl", parseAsString);
  // When set, the user explicitly wants to add a new organization, so we must
  // not auto-authorize into their existing single fiscalization (skip-pin flow).
  const [shouldCreateNewOrganization, setShouldCreateNewOrganization] =
    useQueryState("createNew", parseAsBoolean.withDefault(false));
  const preselectedFiscalizationIdParam = searchParams.get("fscid");
  const preselectedFiscalizationId = preselectedFiscalizationIdParam
    ? Number(preselectedFiscalizationIdParam)
    : undefined;
  const hasValidPreselectedFiscalizationId =
    preselectedFiscalizationId != null &&
    !isNaN(preselectedFiscalizationId) &&
    Number.isFinite(preselectedFiscalizationId);

  const isOnTaxRegisterSetupPage = pathname.startsWith(
    "/dashboard/tax-register/settings/setup",
  );

  const accountInfo = useAccountInfo((state) => state.accountInfo);

  const navigate = useNavigate();

  const { data: posAuthData, refetch: refetchPosAuth } =
    api.cookie.useGetPosToken();

  const { isFeatureEnabled } = useClientFeature();

  const {
    mutateAsync: verifyUserPIN,
    isPending: isVerifyingUserPIN,
    processedErrorMessage: verifyUserPINErrorMessage,
  } = api.user.useVerifyUserPIN();

  const {
    mutateAsync: organizationSetupProgress,
    isPending: isCheckingSetupProgress,
  } = api.taxRegister.useGetOrganizationSetupProgressMutation();

  const {
    onOpen: openCreatePinDialog,
    isOpen: isCreatePinDialogOpen,
    onClose: closeCreatePinDialog,
  } = useDisclosure();

  const inputRef = useRef<HTMLInputElement | null>(null);

  const {
    data: taxRegisterSettings,
    isFetching: isFetchingTaxRegisterSettings,
    processedErrorMessage: taxRegisterSettingsErrorMessage,
  } = api.taxRegister.useGetTaxRegisterSettings();

  const { data: users, isFetching: isUsersFetching } = api.user.useUserList();

  const hasNoAuthorizableUsers = computeHasNoAuthorizableUsers(users);

  const canViewUsersPage = useUserPermissionsStore((state) =>
    Boolean(state.permissions.view_users_page),
  );

  const { logout } = useLogout();

  const form = useForm<TaxRegisterAuthorizationForm>({
    initialValues: {
      PIN: "",
      userId: null,
      fiscalizationId: hasValidPreselectedFiscalizationId
        ? preselectedFiscalizationId
        : undefined,
      taxSystem: accountInfo?.defaultTaxSystem ?? "furs",
    },

    onValuesChange: (values, previous) => {
      const didUserChange = values.userId !== previous.userId;
      if (didUserChange) {
        form.setFieldValue("PIN", "");
        form.setFieldValue("fiscalizationId", undefined);
        form.setFieldValue("taxSystem", undefined);
      }
    },

    validate: {
      PIN: (value) =>
        value || taxRegisterSettings?.skipAuthorizationGlobally
          ? null
          : t`Obvezno polje`,
      userId: (value) => (value ? null : t`Obvezno polje`),
      taxSystem: (value, values) =>
        values.fiscalizationId === null && !value ? t`Obvezno polje` : null,
    },
  });

  const {
    data: fiscalizations,
    isFetching: isFetchingFiscalizations,
    processedErrorMessage: getFiscalizationsErrorMessage,
  } = api.taxRegister.useGetTaxRegisterFiscalizations({
    query: { restrictToUserId: form.getValues().userId ?? undefined },
  });

  const handleSubmit = async (data: TaxRegisterAuthorizationForm) => {
    const response = await verifyUserPIN({
      PIN: data.PIN,
      type: "POS",
      userId: data.userId!,
      fiscalizationId: data.fiscalizationId,
      taxSystem: data.fiscalizationId === null ? data.taxSystem : undefined,
    });

    if (
      !response.canViewTaxRegisterSettings &&
      pathname.startsWith("/dashboard/tax-register/settings")
    ) {
      addToast({
        title: t`Nimate dovoljenja`,
        description: t`Nimate dovoljenja za dostop do nastavitev davčne blagajne`,
        color: "danger",
      });
      navigate("/dashboard/", { replace: true });
      return;
    }

    if (requiresSetupCompleted) {
      const setupProgress = await organizationSetupProgress();
      if (!setupProgress?.isSetupCompleted) {
        const params = new URLSearchParams({ auid: String(data.userId) });
        if (data.fiscalizationId != null) {
          params.set("fscid", String(data.fiscalizationId));
        }

        navigate(`/dashboard/tax-register/settings/setup?${params.toString()}`);
        return;
      }
    }

    queryClient.invalidateQueries({ queryKey: ["user-permissions"] });
    queryClient.invalidateQueries({ queryKey: ["getOrganization"] });
    queryClient.invalidateQueries({
      queryKey: ["getOrganizationTaxRegisterSettings"],
    });
    queryClient.invalidateQueries({
      queryKey: ["getOrganizationSetupProgress"],
    });
    queryClient.invalidateQueries({
      queryKey: ["getOrganizationBusinessPremise"],
    });
    queryClient.invalidateQueries({
      queryKey: ["getOrganizationElectronicDevice"],
    });
    queryClient.invalidateQueries({
      queryKey: ["getPosToken"],
    });
    queryClient.invalidateQueries({
      queryKey: ["services"],
    });
    queryClient.invalidateQueries({
      queryKey: ["categories"],
    });
    queryClient.invalidateQueries({
      queryKey: ["getProducts"],
    });
    queryClient.invalidateQueries({
      queryKey: ["getProductCategories"],
    });

    await refetchPosAuth();
    handleSuccessfulAuthorization?.();

    // Clear the create-new intent so a later token expiry doesn't reopen the
    // dialog in create-new mode after the organization has been created.
    setShouldCreateNewOrganization(null);

    form.reset();
  };

  useEffect(() => {
    if (form.getValues().userId != null) {
      if (!isMobileSafari && !isNativePlatform()) {
        inputRef.current?.focus();
      }
      return;
    }

    if (!users) return;

    if (preselectedUserId) {
      const user = users?.users?.find(
        (u) => u.userId === Number(preselectedUserId) && canAuthorizeOnPos(u),
      );
      if (user) {
        form.setFieldValue("userId", user.userId);
      }

      return;
    }

    // Try to use logged-in user if they have a tax number
    if (accountInfo?.userId) {
      const loggedInUser = users?.users?.find(
        (u) => u.userId === Number(accountInfo.userId) && canAuthorizeOnPos(u),
      );
      if (loggedInUser) {
        form.setFieldValue("userId", loggedInUser.userId);
        return;
      }
    }

    const firstUserWithTaxNumber = users?.users?.find(canAuthorizeOnPos);
    if (firstUserWithTaxNumber) {
      form.setFieldValue("userId", firstUserWithTaxNumber.userId);
    }
  }, [users, form, accountInfo]);

  useEffect(() => {
    if (!taxRegisterSettings || !users || posAuthData != null) return;

    // The user came here to add a new organization; never auto-authorize into
    // the existing one, otherwise the create-new flow is unreachable.
    if (shouldCreateNewOrganization) return;

    const posTaxUsers = users?.users?.filter(canAuthorizeOnPos) ?? [];

    if (
      taxRegisterSettings.skipAuthorizationGlobally &&
      posTaxUsers.length === 1 &&
      fiscalizations?.fiscalizations.length === 1
    ) {
      handleSubmit({
        userId: posTaxUsers[0]?.userId,
        PIN: "",
        fiscalizationId: fiscalizations?.fiscalizations[0]?.id ?? undefined,
      });
    }
  }, [
    taxRegisterSettings,
    users,
    posAuthData,
    fiscalizations,
    shouldCreateNewOrganization,
  ]);

  // Set timeout to refresh on token expiration
  useEffect(() => {
    if (!posAuthData) return;

    const timeout = setTimeout(() => {
      refetchPosAuth();
    }, posAuthData.expiresIn);

    return () => {
      clearTimeout(timeout);
    };
  }, [posAuthData]);

  useEffect(() => {
    if (shouldCreateNewOrganization) {
      // Keep create-new mode sticky: changing the selected employee resets
      // fiscalizationId to undefined via onValuesChange, so re-apply null here
      // (this effect re-runs on userId changes) to avoid submitting undefined.
      form.setFieldValue("fiscalizationId", null);
      if (form.getValues().taxSystem == null) {
        form.setFieldValue(
          "taxSystem",
          accountInfo?.defaultTaxSystem ?? "furs",
        );
      }
      return;
    }

    if (hasValidPreselectedFiscalizationId) {
      form.setFieldValue("fiscalizationId", preselectedFiscalizationId);
      return;
    }

    if (!fiscalizations) return;

    if (fiscalizations.fiscalizations.length > 0) {
      form.setFieldValue(
        "fiscalizationId",
        fiscalizations.fiscalizations[0].id,
      );
    }

    // If no fiscalizations are found, set the fiscalization id to null so that the user can create a new organization
    if (fiscalizations.fiscalizations.length === 0) {
      form.setFieldValue("fiscalizationId", null);
      form.setFieldValue("taxSystem", accountInfo?.defaultTaxSystem ?? "furs");
    }
  }, [
    form.values.userId,
    accountInfo?.defaultTaxSystem,
    fiscalizations,
    shouldCreateNewOrganization,
  ]);

  const createOrganizationTaxSystems: TaxSystem[] = ["furs", "fina"];

  const {
    data: userHasPin,
    isFetching: isUserHasPinFetching,
    refetch: refetchUserHasPin,
  } = api.user.useGetUserHasPin({
    pinType: "POS",
    userId: form.getValues().userId || undefined,
  });

  const handleAuthorizeModalClose = (open: boolean) => {
    if (!open) {
      // Already-authorized users only see this dialog when they chose to add a
      // new organization; cancelling just clears that intent and keeps them on
      // the setup page instead of redirecting away.
      if (posAuthData?.userId != null) {
        setShouldCreateNewOrganization(null);
        return;
      }

      if (onCancel) {
        onCancel();
        return;
      }

      const resolvedRedirectUrl = redirectUrlProp ?? redirectUrl;

      if (resolvedRedirectUrl) {
        navigate(resolvedRedirectUrl.replace(window.location.origin, ""), {
          replace: true,
        });
        return;
      }

      navigate("/dashboard/", { replace: true });
    }
  };

  if (isCreatePinDialogOpen && form.getValues().userId != null) {
    return (
      <POSPinSetupDialog
        userId={form.getValues().userId!}
        handleCreatedPin={() => {
          refetchUserHasPin();
          closeCreatePinDialog();
        }}
        handleAuthorizeModalClose={() => {
          closeCreatePinDialog();
        }}
      />
    );
  }

  // Normally the dialog only shows while unauthenticated. It also opens for an
  // already-authenticated (e.g. skip-pin) user who explicitly chose to add a
  // new organization, so they can step into create-new mode without losing
  // their authorization.
  const isAuthenticated = posAuthData?.userId != null;

  // Early return if already authenticated and not adding a new organization -
  // don't render the modal at all.
  if (isAuthenticated && !shouldCreateNewOrganization) {
    return null;
  }

  return (
    <Modal
      isOpen={!isAuthenticated || shouldCreateNewOrganization}
      onOpenChange={handleAuthorizeModalClose}
    >
      <ModalContent>
        {(onClose) => (
          <form onSubmit={form.onSubmit(handleSubmit)}>
            <ModalHeader>
              <Trans>Avtorizacija</Trans>
            </ModalHeader>

            {hasNoAuthorizableUsers ? (
              <>
                <ModalBody className="flex flex-col gap-4">
                  <LimeAlert
                    color="warning"
                    className="w-full"
                    data-identifier="pos-no-authorizable-users"
                    message={t`Za avtorizacijo potrebujete vsaj enega zaposlenega z vpisano davčno številko, ki je prikazan na blagajni in ima dovoljenje za izdajo računov.`}
                  />
                  {!canViewUsersPage && (
                    <p className="text-foreground-500 text-sm">
                      <Trans>
                        Za urejanje zaposlenih se obrnite na skrbnika računa.
                      </Trans>
                    </p>
                  )}
                </ModalBody>

                <ModalFooter>
                  <Button
                    variant="light"
                    data-identifier="pos-no-authorizable-users-logout"
                    onPress={() => logout("pos authorization dialog")}
                  >
                    <Trans>Odjava</Trans>
                  </Button>
                  {canViewUsersPage && (
                    <Button
                      color="primary"
                      data-identifier="pos-no-authorizable-users-go-to-users"
                      onPress={() => navigate("/dashboard/users")}
                    >
                      <Trans>Uredi zaposlene</Trans>
                    </Button>
                  )}
                </ModalFooter>
              </>
            ) : (
              <>
                <ModalBody className="flex flex-col items-center justify-center gap-4">
                  <LimeSelect
                    label={t`Zaposleni`}
                    data-identifier="user-select"
                    items={
                      users?.users?.flatMap((user) =>
                        canAuthorizeOnPos(user)
                          ? [
                              {
                                key: user.userId.toString(),
                                identifier: `user-select-option-${user.userId}`,
                                label: `${user.name} ${user.lastName}`,
                              },
                            ]
                          : [],
                      ) || []
                    }
                    isLoading={
                      isUsersFetching ||
                      isFetchingTaxRegisterSettings ||
                      isVerifyingUserPIN
                    }
                    {...form.getInputProps("userId")}
                  />

                  <LimeSelect
                    label={t`Organizacija`}
                    data-identifier="organization-select"
                    isLoading={isFetchingFiscalizations}
                    placeholder={
                      form.getValues().fiscalizationId == null &&
                      !isFetchingFiscalizations
                        ? t`Ustvari novo organizacijo`
                        : undefined
                    }
                    items={
                      fiscalizations?.fiscalizations.map((fiscalization) => ({
                        key: fiscalization.id.toString(),
                        label:
                          fiscalization.organization?.name ??
                          fiscalization.id.toString(),
                        sectionName: fiscalization.taxSystem.toUpperCase(),
                        endContent: isArchivedOrganization(fiscalization) ? (
                          <Chip
                            size="sm"
                            variant="flat"
                            data-identifier={`organization-archived-${fiscalization.id}`}
                          >
                            <Trans>Arhiv</Trans>
                          </Chip>
                        ) : undefined,
                      })) ?? []
                    }
                    {...form.getInputProps("fiscalizationId")}
                    createNewOption={
                      isOnTaxRegisterSetupPage &&
                      isFeatureEnabled("canHaveMultipleFiscalizations")
                        ? {
                            label: t`Ustvari novo organizacijo`,
                            sectionName: t`Nova organizacija`,
                            onCreateNew: () => {
                              const currentTaxSystem =
                                form.getValues().taxSystem ??
                                accountInfo?.defaultTaxSystem ??
                                "furs";

                              form.setFieldValue("fiscalizationId", null);
                              form.setFieldValue("taxSystem", currentTaxSystem);
                            },
                          }
                        : undefined
                    }
                  />

                  {isOnTaxRegisterSetupPage &&
                    ((fiscalizations &&
                      fiscalizations?.fiscalizations.length > 0 &&
                      isFeatureEnabled("canHaveMultipleFiscalizations")) ||
                      fiscalizations?.fiscalizations.length === 0) &&
                    form.getValues().fiscalizationId === null && (
                      <LimeSelect
                        label={t`Davčni sistem`}
                        data-identifier="organization-tax-system-select"
                        items={createOrganizationTaxSystems.map(
                          (taxSystem) => ({
                            key: taxSystem,
                            label: taxSystem.toUpperCase(),
                          }),
                        )}
                        value={form.getValues().taxSystem}
                        onSingleItemChange={(value) => {
                          form.setFieldValue("taxSystem", value as TaxSystem);
                        }}
                        error={form.errors.taxSystem}
                      />
                    )}

                  <LimeAlert message={getFiscalizationsErrorMessage} />

                  {form.getValues().userId != null &&
                    !isUserHasPinFetching &&
                    !userHasPin?.hasPin && (
                      <div className="flex flex-col items-center gap-2">
                        <p>
                          <Trans>Nimate PIN kode</Trans>
                        </p>
                        <Button onPress={openCreatePinDialog} color="primary">
                          <Trans>Ustvari PIN kodo</Trans>
                        </Button>
                      </div>
                    )}
                  {!taxRegisterSettings?.skipAuthorizationGlobally &&
                    (isUserHasPinFetching || userHasPin?.hasPin) && (
                      <div className="flex flex-col items-center">
                        <p className="mb-2 font-semibold">
                          <Trans>PIN Koda</Trans>
                        </p>

                        <div className="relative">
                          <LimeInputOtp
                            length={4}
                            {...form.getInputProps("PIN")}
                            onComplete={() => {
                              if (form.isValid()) {
                                handleSubmit(form.values);
                              }
                            }}
                            type="password"
                            variant="bordered"
                            ref={inputRef}
                            data-identifier="pos-pin-input"
                            classNames={{
                              base: "justify-center items-center",
                            }}
                            isDisabled={
                              !userHasPin?.hasPin || isUserHasPinFetching
                            }
                          />

                          {isUserHasPinFetching && (
                            <div className="absolute top-1/2 left-1/2 flex -translate-x-1/2 -translate-y-1/2 items-center justify-center">
                              <Spinner variant="dots" />
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                  <LimeAlert
                    message={verifyUserPINErrorMessage}
                    className="w-full"
                    color="danger"
                  />
                  <LimeAlert
                    message={taxRegisterSettingsErrorMessage}
                    className="w-full"
                    color="danger"
                  />
                </ModalBody>

                <ModalFooter>
                  <Button
                    type="submit"
                    isDisabled={
                      isUsersFetching ||
                      isUserHasPinFetching ||
                      isCheckingSetupProgress
                    }
                    isLoading={isVerifyingUserPIN || isCheckingSetupProgress}
                    color="primary"
                  >
                    <Trans>Potrdi</Trans>
                  </Button>
                </ModalFooter>
              </>
            )}
          </form>
        )}
      </ModalContent>
    </Modal>
  );
};
